import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { directoryApi, type GridLayoutLayers, type GridLayoutScope } from '../client';
import {
  clampWidth,
  gridLayoutJson,
  resolveGridColumns,
  type GridColumnDef,
  type ResolvedGridColumn,
} from '../gridLayout';

/** Stable query-key factory for grid layouts. */
export const gridLayoutKeys = {
  all: ['directory', 'grid-layout'] as const,
  byGrid: (organisationId: string, app: string, gridKey: string) =>
    ['directory', 'grid-layout', organisationId, app, gridKey] as const,
};

const EMPTY: GridLayoutLayers = {
  organisationLayoutJson: null,
  userLayoutJson: null,
  canEditOrganisationDefault: false,
};

/** Browser copy of the last layers seen, so the grid opens in its layout before the server answers. */
const cacheKey = (organisationId: string, app: string, gridKey: string) =>
  `my.gridLayout.${organisationId}.${app}.${gridKey}`;

function readCache(key: string): GridLayoutLayers | undefined {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as GridLayoutLayers) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(key: string, layers: GridLayoutLayers) {
  try {
    window.localStorage.setItem(key, JSON.stringify(layers));
  } catch {
    // Private window / storage full — the server copy is what counts.
  }
}

/**
 * One grid's column layout — the code's columns with the organisation default
 * and the signed-in user's own layout laid over them (see `gridLayout.ts`).
 *
 * <p>
 * `setColumns` applies at once and saves the user's layout in the background:
 * debounced, one save at a time, the latest always winning. A failed save
 * reloads the layout from the server and reports it in `saveError`.
 * </p>
 */
export function useGridLayout(
  organisationId: string | null,
  app: string,
  gridKey: string,
  defs: GridColumnDef[],
) {
  const qc = useQueryClient();
  const key = useMemo(
    () =>
      organisationId ? gridLayoutKeys.byGrid(organisationId, app, gridKey) : gridLayoutKeys.all,
    [organisationId, app, gridKey],
  );
  const storageKey = organisationId ? cacheKey(organisationId, app, gridKey) : null;

  const query = useQuery<GridLayoutLayers, Error>({
    queryKey: key,
    queryFn: async () => {
      const layers = await directoryApi.getGridLayout(organisationId!, app, gridKey);
      if (storageKey) writeCache(storageKey, layers);
      return layers;
    },
    enabled: !!organisationId,
    initialData: () => (storageKey ? readCache(storageKey) : undefined),
    // The browser copy paints the grid; the server is still asked.
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60 * 1000,
  });
  const layers = query.data ?? EMPTY;

  const columns = useMemo<ResolvedGridColumn[]>(
    () => resolveGridColumns(defs, layers.organisationLayoutJson, layers.userLayoutJson),
    [defs, layers.organisationLayoutJson, layers.userLayoutJson],
  );
  const visibleColumnIds = useMemo(
    () => columns.filter((c) => c.visible).map((c) => c.id),
    [columns],
  );

  const setLayers = useCallback(
    (update: (prev: GridLayoutLayers) => GridLayoutLayers) => {
      qc.setQueryData<GridLayoutLayers>(key, (prev) => {
        const next = update(prev ?? EMPTY);
        if (storageKey) writeCache(storageKey, next);
        return next;
      });
    },
    [qc, key, storageKey],
  );

  // ── Background save of the user's own layout ───────────────────────────────
  const [saveError, setSaveError] = useState<string | null>(null);
  const pending = useRef<string | null>(null);
  const saving = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  const flush = useCallback(async () => {
    if (!organisationId || saving.current || pending.current === null) return;
    const layoutJson = pending.current;
    pending.current = null;
    saving.current = true;
    try {
      await directoryApi.saveGridLayout({
        organisationId,
        app,
        gridKey,
        scope: 'Mine',
        layoutJson,
      });
      setSaveError(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Couldn’t save your columns.');
      void qc.invalidateQueries({ queryKey: key });
    } finally {
      saving.current = false;
      if (pending.current !== null) void flush();
    }
  }, [organisationId, app, gridKey, qc, key]);

  // Leaving the screen mid-debounce still saves.
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      void flush();
    },
    [flush],
  );

  // A move or a tick from the chooser says nothing about widths — keep them.
  const widthById = useMemo(
    () => new Map(columns.filter((c) => c.width).map((c) => [c.id, c.width!])),
    [columns],
  );

  const setColumns = useCallback(
    (next: { id: string; visible: boolean; width?: number }[]) => {
      const layoutJson = gridLayoutJson(
        next.map((c) => ({ ...c, width: 'width' in c ? c.width : widthById.get(c.id) })),
      );
      // Nothing changed (e.g. a view re-applied as the screen opens) — no save.
      if (layoutJson === qc.getQueryData<GridLayoutLayers>(key)?.userLayoutJson) return;
      setLayers((prev) => ({ ...prev, userLayoutJson: layoutJson }));
      pending.current = layoutJson;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), 600);
    },
    [setLayers, flush, widthById, qc, key],
  );

  /** Size one column (null = fit its content again). */
  const setWidth = useCallback(
    (id: string, width: number | null) =>
      setColumns(
        columns.map((c) => ({
          id: c.id,
          visible: c.visible,
          width: c.id === id ? (width == null ? undefined : clampWidth(width)) : c.width,
        })),
      ),
    [columns, setColumns],
  );

  // While a header edge is being dragged the width follows the pointer here,
  // and is only saved when the drag ends.
  const [dragging, setDragging] = useState<{ id: string; width: number } | null>(null);
  const previewWidth = useCallback(
    (id: string, width: number | null) =>
      setDragging(width == null ? null : { id, width: clampWidth(width) }),
    [],
  );
  const widthOf = useCallback(
    (id: string): number | undefined => (dragging?.id === id ? dragging.width : widthById.get(id)),
    [dragging, widthById],
  );

  // ── Resets and the organisation default ────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (work: () => Promise<unknown>, fallback: string) => {
      setBusy(true);
      try {
        await work();
        setSaveError(null);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : fallback);
      } finally {
        setBusy(false);
        void qc.invalidateQueries({ queryKey: key });
      }
    },
    [qc, key],
  );

  const reset = useCallback(
    (scope: GridLayoutScope) =>
      run(async () => {
        if (scope === 'Mine') {
          window.clearTimeout(timer.current);
          pending.current = null;
          setLayers((prev) => ({ ...prev, userLayoutJson: null }));
        } else {
          setLayers((prev) => ({ ...prev, organisationLayoutJson: null }));
        }
        await directoryApi.resetGridLayout(organisationId!, app, gridKey, scope);
      }, 'Couldn’t reset the columns.'),
    [run, setLayers, organisationId, app, gridKey],
  );

  /** Make the columns as they are now everyone's starting point. */
  const saveAsOrganisationDefault = useCallback(() => {
    const layoutJson = gridLayoutJson(columns);
    return run(async () => {
      setLayers((prev) => ({ ...prev, organisationLayoutJson: layoutJson }));
      await directoryApi.saveGridLayout({
        organisationId: organisationId!,
        app,
        gridKey,
        scope: 'Organisation',
        layoutJson,
      });
    }, 'Couldn’t save the organisation default.');
  }, [run, setLayers, columns, organisationId, app, gridKey]);

  return {
    /** Every column in display order, visible or not — for the chooser. */
    columns,
    /** The ids to render, in order. */
    visibleColumnIds,
    setColumns,
    /** The column's width in pixels, or undefined to fit its content. */
    widthOf,
    /** Follow a header-edge drag without saving each step. */
    previewWidth,
    /** The columns as they are now, as a stored layout (for saving a view). */
    currentLayoutJson: gridLayoutJson(columns),
    /** A stored layout as this grid would show it — compares a view with the screen. */
    resolvedLayoutJson: (json: string) =>
      gridLayoutJson(resolveGridColumns(defs, layers.organisationLayoutJson, json)),
    /** Put a stored layout (a view's) on the screen as my own. */
    applyLayoutJson: (json: string) =>
      setColumns(resolveGridColumns(defs, layers.organisationLayoutJson, json)),
    /** Every column fits its content again. */
    resetWidths: () =>
      setColumns(columns.map((c) => ({ id: c.id, visible: c.visible, width: undefined }))),
    /** Keep a width (null = fit content again); ends any drag preview. */
    setWidth: (id: string, width: number | null) => {
      setDragging(null);
      setWidth(id, width);
    },
    resetMine: () => reset('Mine'),
    saveAsOrganisationDefault,
    resetOrganisationDefault: () => reset('Organisation'),
    hasOwnLayout: !!layers.userLayoutJson,
    hasOrganisationLayout: !!layers.organisationLayoutJson,
    canEditOrganisationDefault: layers.canEditOrganisationDefault,
    busy,
    saveError,
  };
}
